<template>
  <div>
    <table class="table is-striped is-fullwidth is-bordered is-hoverable">
      <thead>
        <tr>
          <th>File Name</th>
          <th v-if="mode==='advisor'">Status</th>
          <th>Uploaded At</th>
          <th>Classification</th>
          <th>Summary</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(doc, index) in documents">
          <tr :key="doc.external_id || `upload-${index}`" :data-document-id="doc.external_id">
            <!-- File Name -->
            <td class="name">{{ cleanFileName(doc.filename || doc.name) }}</td>

            <!-- Validation Status -->
            <td v-if="mode === 'advisor'" class="status">
              <template v-if="!isPast">
                <LoaderWithText v-if="!doc.status  || doc.status && doc.status.toLowerCase() === 'processing' || doc.status && doc.status.toLowerCase() === 'pending'">
                  Processing 
                </LoaderWithText>
                <Badge v-else-if="doc.status && doc.status.toLowerCase() === 'failed' || (doc.error & doc.status && doc.status.toLowerCase() != 'completed')" status="danger">
                  Error: {{ doc.error || 'Error processing document'}}
                </Badge>
                <Badge v-else status="success">
                  Complete
                </Badge>
              </template>

              <!-- For documents that are processing, show the status -->
              <template v-if="doc.status === 'processing' || doc.status === 'pending || !doc.status '">
                <Badge status="warning">
                  {{ doc.status === 'processing' ? 'Processing' : 'Pending' }}
                </Badge>
              </template>
              <template v-if="doc.status && doc.status.toLowerCase()=== 'failed'">
                <Badge status="danger"
                  v-text="'Document processing failed'">
                </Badge>
              </template>
              <template v-else-if="
                  doc.status &&
                  doc.status.toLowerCase() !== 'processing' &&
                  doc.status.toLowerCase() !== 'pending' &&
                  doc.status.toLowerCase() !== 'failed'
                ">
                <div @click="toggleValidationDetails(doc)">
                  <strong :class="{
                    'has-text-success': Array.isArray(doc.validation) && doc.validation.every(v => v.passed),
                    'has-text-danger': !Array.isArray(doc.validation) || !doc.validation.every(v => v.passed)
                  }">
                    {{ Array.isArray(doc.validation) && doc.validation.every(v => v.passed) ? 'All Checks Passed' : '> Review DocCheckup' }}
                  </strong>
                </div>

                <ul v-if="areValidationDetailsExpanded(doc)" class="mt-2">
                  <li v-for="check in doc.validation" :key="check.name" class="ml-4">
                  <strong :class="{ 'has-text': !check.passed }">
                    • {{ check.name }}
                  </strong>
                  <span>: {{ check.details }}</span>
                  </li>
                </ul>
              </template>
            </td>

            <!-- Uploaded At -->
            <td class="date">{{ formatDate(doc.uploaded_at) }}</td>

            <!-- Classification -->
            <td class="classification">
              <span v-if="doc.classification">
                {{ doc.classification }}
              </span>
              <Badge v-else-if="isPast">
                Unclassified
              </Badge>
              <Badge v-else status="warning">
                {{doc.status}}
              </Badge>
            </td>

            <!-- Summary -->
            <td class="summary">
              <span v-if="!isSummaryExpanded(doc)">
                {{ doc.info && doc.info.length > 100 ? doc.info.substring(0, 100) + '…' : doc.info || 'Pending' }}
                <a
                  v-if="doc.info && doc.info.length > 100"
                  href="#"
                  @click.prevent="toggleSummary(doc)"
                  class="has-text-link"
                >
                  Show More
                </a>
              </span>
              <span v-else>
                {{ doc.info || doc.status }}
                <a
                  href="#"
                  @click.prevent="toggleSummary(doc)"
                  class="has-text-link"
                >
                  Show Less
                </a>
              </span>
            </td>

            <!-- Actions -->
            <td class="actions" :class="{ 'actions-client': mode === 'client' }">
              <Buttons v-if="doc.status != 'processing' && doc.status != 'pending'" noWrap gap="1">
                <router-link
                  v-if="mode==='advisor' && doc.status === 'completed'"
                  :to="{
                    name: 'document-view',
                    params: { id: doc.external_id }, // Pass the document external ID
                    query: { entityId: entityExternalId } // Pass the entity ID
                  }"
                  v-slot="{ href }"
                >
                  <Button :href="href" target="_blank" variant="primary" size="3">View</Button>
                </router-link>

                <Button
                  v-if="mode==='advisor' && doc.status === 'completed'"
                  @click.prevent="downloadParsedDocument(doc.external_id)"
                  iconBefore="fas fa-download"
                  size="3"
                  title="Download as spreadsheet"
                >
                  Spreadsheet
                </Button>

                <Button
                  v-if="isPast"
                  @click="$emit('delete-requested', doc.external_id, doc.filename)"
                  title="Delete Document"
                  iconOnly="fas fa-trash"
                  size="3"
                />
              </Buttons>

              <!-- Portfolio Actions for Brokerage Statements (Second Line) -->
              <Buttons v-if="mode==='advisor' && isBrokerageStatement(doc) && doc.status === 'completed'" gap="1" style="margin-top: 0.5rem;">
                <!-- Add to Portfolio Button -->
                <Button
                  v-if="!doc.portfolio_added_to_entity"
                  @click="addPortfolioToEntity(doc)"
                  :disabled="isAddingPortfolio[doc.external_id]"
                  variant="secondary"
                  outlined
                  size="3"
                  title="Copy all holdings from this brokerage statement to the client's portfolio"
                >
                  <template v-if="isAddingPortfolio[doc.external_id]">
                    <i class="fas fa-spinner fa-spin"></i> Adding...
                  </template>
                  <template v-else>
                    <i class="fas fa-plus"></i> Add to Portfolio
                  </template>
                </Button>

                <!-- Undo Button (shown when already added) -->
                <Button
                  v-else
                  @click="undoPortfolioAddition(doc)"
                  :disabled="isUndoingPortfolio[doc.external_id]"
                  variant="link"
                  size="3"
                  :title="`Added on ${formatDateShort(doc.portfolio_added_at)} - Click to remove these holdings from the portfolio`"
                >
                  <template v-if="isUndoingPortfolio[doc.external_id]">
                    <i class="fas fa-spinner fa-spin"></i> Removing...
                  </template>
                  <template v-else>
                    <i class="fas fa-undo"></i> Undo Adding to Portfolio
                  </template>
                </Button>
              </Buttons>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { Badge, Button, Buttons, LoaderWithText } from '@stakeui';
import { cleanFileName } from '../../../../shared/Util';
import { ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  isPast: {
    type: Boolean,
    default: false,
  },
  documents: {
    type: Array,
    required: true,
  },
  entityExternalId: {
    type: String,
    required: true,
  },
  mode: {
    type: String,
    default: 'advisor',
    validator: (value) => ['advisor', 'client'].includes(value)
  },
});

// Polling functionality
const POLLING_INTERVAL = 10000; // Check every 10 seconds
const pollingTimer = ref(null);
const expandedSummaries = ref(new Set());
const expandedValidationDetails = ref(new Set());

// Portfolio addition functionality
const isAddingPortfolio = ref({});
const isUndoingPortfolio = ref({});

const BROKERAGE_STATEMENT_TYPES = [
  'Brokerage Account Statement',
  'Brokerage Statement',
  'Investment Account Statement',
  '401k Statement',
  'IRA Statement',
  'Screenshot of Holdings Portal'
];

// Check if any documents are in processing state
const hasProcessingDocuments = () => {
  return props.documents.some(doc => 
    doc.status === 'processing' || 
    doc.status === 'pending'
  );
};

const emit = defineEmits(['delete-requested', 'document-done', 'portfolio-added', 'portfolio-removed']);
// Start polling for document updates
const startPolling = () => {
  // Clear any existing timer first
  stopPolling();
  
  // Set new polling timer
  pollingTimer.value = setInterval(() => {
    // Emit event to trigger parent component to refresh documents
    emit('document-done');
  }, POLLING_INTERVAL);
};

// Stop polling
const stopPolling = () => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value);
    pollingTimer.value = null;
  }
};

const toggleSummary = (doc) => {
  const docId = doc.external_id || doc.name;
  const newSet = new Set(expandedSummaries.value);
  
  if (newSet.has(docId)) {
    newSet.delete(docId);
  } else {
    newSet.add(docId);
  }
  
  expandedSummaries.value = newSet;
};

const isSummaryExpanded = (doc) => {
  const docId = doc.external_id || doc.name;
  return expandedSummaries.value.has(docId);
};

const toggleValidationDetails = (doc) => {
  const docId = doc.external_id || doc.name;
  const newSet = new Set(expandedValidationDetails.value);
  
  if (newSet.has(docId)) {
    newSet.delete(docId);
  } else {
    newSet.add(docId);
  }
  
  expandedValidationDetails.value = newSet;
};

const areValidationDetailsExpanded = (doc) => {
  const docId = doc.external_id || doc.name;
  return expandedValidationDetails.value.has(docId);
};

// Watch for changes in the documents array
watch(
  () => props.documents,
  () => {
    // If we have processing documents, start/continue polling
    if (hasProcessingDocuments()) {
      startPolling();
    } else {
      stopPolling();
    }
  },
  { deep: true, immediate: true }
);

// Start polling when component mounts if needed
onMounted(() => {
  if (hasProcessingDocuments()) {
    startPolling();
  }
});

// Clean up when component unmounts
onUnmounted(() => {
  stopPolling();
});

const formatDate = (dateString) => {
  if (!dateString) {
    return '—';
  }
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

const downloadParsedDocument = async (uploadId) => {
  try {
    const response = await fetch(`/api/v1/process/document_xls/?document_id=${uploadId}`, {
      headers: props.headers,
    });
    const data = await response.json();
    console.log('ERROR IN GETTING XLS DATA', data.error);

    if (data.url) {
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.filename || 'parsed_document.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('No URL found in the response');
    }
  } catch (error) {
    console.error('Error downloading parsed document:', error);
  }
}

function isBrokerageStatement(doc) {
  return !!doc.future_model_portfolio;
}

function formatDateShort(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

async function addPortfolioToEntity(doc) {
  const confirmed = confirm(
    `Add holdings from "${doc.filename}" to this client's portfolio?\n\n` +
    `This will copy all holdings from the brokerage statement.`
  );

  if (!confirmed) return;

  isAddingPortfolio.value[doc.external_id] = true;

  try {
    const response = await fetch(
      `/api/v1/documents/${doc.external_id}/add-portfolio-to-entity/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken()
        },
        body: JSON.stringify({
          entity_id: props.entityExternalId
        })
      }
    );

    const data = await response.json();

    if (response.ok) {
      doc.portfolio_added_to_entity = props.entityExternalId;
      doc.portfolio_added_at = data.added_at;

      alert(`✅ Success!\n\n${data.holdings_added} holdings added to portfolio.`);
      emit('portfolio-added', doc);

      // Navigate to the holdings tab
      window.location.href = `/ui/advisor/entity/${props.entityExternalId}/portfolio`;
    } else {
      alert(`❌ Error: ${data.error || 'Failed to add portfolio'}`);
    }
  } catch (error) {
    console.error('Error adding portfolio:', error);
    alert('❌ Failed to add portfolio. Please try again.');
  } finally {
    isAddingPortfolio.value[doc.external_id] = false;
  }
}

async function undoPortfolioAddition(doc) {
  const warning =
    `⚠️  WARNING: UNDO PORTFOLIO ADDITION\n\n` +
    `This will REMOVE all holdings from "${doc.filename}"\n` +
    `that were added to this client's portfolio.\n\n` +
    `The holdings will be DELETED.\n\n` +
    `Are you absolutely sure?`;

  if (!confirm(warning)) return;

  isUndoingPortfolio.value[doc.external_id] = true;

  try {
    const response = await fetch(
      `/api/v1/documents/${doc.external_id}/undo-portfolio-addition/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken()
        }
      }
    );

    const data = await response.json();

    if (response.ok) {
      alert(`✅ Success!\n\n${data.holdings_removed} holdings removed.`);

      // Navigate to the holdings tab
      window.location.href = `/ui/advisor/entity/${props.entityExternalId}/portfolio`;
    } else {
      alert(`❌ Error: ${data.error || 'Failed to undo'}`);
    }
  } catch (error) {
    console.error('Error undoing:', error);
    alert('❌ Failed to undo. Please try again.');
  } finally {
    isUndoingPortfolio.value[doc.external_id] = false;
  }
}

function getCsrfToken() {
  let token = document.querySelector('[name=csrfmiddlewaretoken]')?.value;
  if (!token) {
    token = document.querySelector('meta[name="csrf-token"]')?.content;
  }
  return token;
}

</script>

<style scoped>
.name {
  word-break: break-word;
  width: 20%;
}

.status {
  width: 10em;
}

.date {
  font-size: 0.875em;
  width: 9em;
}

.classification {
  width: 10em;
}

.summary {
  width: 30em;
}

.actions {
  width: 14em;
}

/* Client mode - narrow actions column */
.actions.actions-client {
  width: 5em;
}

</style>
